import { useEffect } from "react";
import {
  createBrowserRouter,
  Outlet,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";

import customerRoutes from "./CustomerRoutes";
import publicRoutes from "./PublicRoutes";
import sellerRoutes from "./SellerRoutes";
import salesRepRoutes from "./SalesRepRoutes";
import Loader from "../common/Loader";
import AppLayout from "../components/layout/AppLayout";
import ProtectedRoute from "../components/protectedRoute/ProtectedRoute";
import { useRole } from "../context/RoleContext";
import { initGA, logPageView } from "../ga";
import { useAppSelector } from "../redux/hooks";
import LandingRoot from "../landing/LandingRoot";
import LandingHomePage from "../landing/pages/HomePage";
import LandingPricingPage from "../landing/pages/PricingPage";
import LandingTrustPage from "../landing/pages/TrustPage";
import LandingBlogPage from "../landing/pages/BlogPage";
import LandingBlogArticlePage from "../landing/pages/BlogArticlePage";
import LandingContactPage from "../landing/pages/ContactPage";
import LandingRegisterPage from "../landing/pages/RegisterPage";
import LandingTermsPage from "../landing/pages/TermsPage";
import LandingPrivacyPage from "../landing/pages/PrivacyPage";
import LoginScreen from "../components/loginScreen/LoginScreen";
import SignupScreenConfirmation from "../components/loginScreen/SignupScreenConfirmation";
import { normalizeRole } from "../utils/roleUtils";

const NotFound = () => {
  return (
    <div>
      <h1>404 - Page Not Found</h1>
      <p>The page you are looking for does not exist.</p>
    </div>
  );
};

const RoleBasedRoute = ({ allowedRole }: { allowedRole: string }) => {
  const { role } = useRole();

  const mappedRole = normalizeRole(role);

  if (mappedRole === allowedRole) {
    return <Outlet />;
  }

  return <Navigate to="/not-authorized" replace />;
};

const LegacyRoleRedirect = ({ from, to }: { from: string; to: string }) => {
  const location = useLocation();
  const redirectedPath = location.pathname.replace(
    new RegExp(`^/${from}(?=/|$)`),
    `/${to}`
  );

  return <Navigate to={`${redirectedPath}${location.search}`} replace />;
};

function AppInitializer() {
  const location = useLocation();
  const loading = useAppSelector((state) => state.app.loading);

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    logPageView(location.pathname + location.search);
  }, [location]);

  return (
    <>
      <Loader show={loading} />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <LandingRoot>
        <LandingHomePage />
      </LandingRoot>
    ),
  },
  {
    path: "/pricing",
    element: (
      <LandingRoot>
        <LandingPricingPage />
      </LandingRoot>
    ),
  },
  {
    path: "/landing/pricing",
    element: (
      <LandingRoot>
        <LandingPricingPage />
      </LandingRoot>
    ),
  },
  {
    path: "/trust",
    element: (
      <LandingRoot>
        <LandingTrustPage />
      </LandingRoot>
    ),
  },
  {
    path: "/blog",
    element: (
      <LandingRoot>
        <LandingBlogPage />
      </LandingRoot>
    ),
  },
  {
    path: "/blog/:slug",
    element: (
      <LandingRoot>
        <LandingBlogArticlePage />
      </LandingRoot>
    ),
  },
  {
    path: "/contact",
    element: (
      <LandingRoot>
        <LandingContactPage />
      </LandingRoot>
    ),
  },
  {
    path: "/register",
    element: (
      <LandingRoot>
        <LandingRegisterPage />
      </LandingRoot>
    ),
  },
  {
    path: "/terms",
    element: (
      <LandingRoot>
        <LandingTermsPage />
      </LandingRoot>
    ),
  },
  {
    path: "/privacy",
    element: (
      <LandingRoot>
        <LandingPrivacyPage />
      </LandingRoot>
    ),
  },
  {
    path: "/signin",
    element: (
      <AppLayout>
        <LoginScreen />
      </AppLayout>
    ),
  },
  {
    path: "/signup-confirmation",
    element: <SignupScreenConfirmation />,
  },

  {
    element: <AppInitializer />,
    children: [
      {
        element: (
          <AppLayout>
            <Outlet />
          </AppLayout>
        ),
        children: [
          ...publicRoutes,
          {
            path: "setpassword",
            element: (
              <Navigate to={`/set-password${window.location.search}`} replace />
            ),
          },
          {
            element: <ProtectedRoute />,
            children: [
              {
                path: "customer/*",
                element: (
                  <LegacyRoleRedirect from="customer" to="maltora-customer" />
                ),
              },
              {
                path: "seller/*",
                element: (
                  <LegacyRoleRedirect from="seller" to="maltora-seller" />
                ),
              },
              {
                path: "sales-rep/*",
                element: (
                  <LegacyRoleRedirect from="sales-rep" to="maltora-sales-rep" />
                ),
              },
              {
                element: <RoleBasedRoute allowedRole="maltora-customer" />,
                children: [...customerRoutes],
              },
              {
                element: <RoleBasedRoute allowedRole="maltora-seller" />,
                children: [...sellerRoutes],
              },
              {
                element: <RoleBasedRoute allowedRole="maltora-sales-rep" />,
                children: [...salesRepRoutes],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

export default function AppRouter() {
  return <RouterProvider router={router} />;
}
